import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import {
    CreatePerformanceTeamReviewSuccess,
    FetchPerformanceTeamReviews,
    FetchPerformanceTeamReviewsFailure,
    FetchPerformanceTeamReviewsSuccess,
    SearchPerformanceTeamReviews,
    SetPerformanceTeamReviews,
} from '../actions';
import { AddPerformanceTeamReviews } from '../actions/add-entities.action';
import { PerformanceTeamReviewApiService } from '../services/performance-team-review.api-service';
import { PerformanceTeamReview } from './performance-team-review.model';
import { PerformanceTeamReviewState } from './performance-team-review.state';
import { PerformanceTeamReviewsStateModel } from './performance-team-reviews.state-model';

@State<PerformanceTeamReviewsStateModel>({
    name: 'performanceTeamReviews',
    defaults: new PerformanceTeamReviewsStateModel(),
})
@Injectable()
export class PerformanceTeamReviewsState extends SubentityState<PerformanceTeamReview> {
    @Selector([PerformanceTeamReviewState.getEntities])
    static getFilteredEntities(state: PerformanceTeamReviewsStateModel, performanceTeamReviews: PerformanceTeamReview[]): PerformanceTeamReview[] {
        if (!performanceTeamReviews) {
            return null;
        }

        return performanceTeamReviews
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, PerformanceTeamReview], b: [number, PerformanceTeamReview]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, PerformanceTeamReview]) => x[1]);
    }

    @Selector()
    static getSearchTerm(state: PerformanceTeamReviewsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: PerformanceTeamReviewsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static isFetchingList(state: PerformanceTeamReviewsStateModel): boolean {
        return state.fetchingList;
    }

    constructor(private _performanceTeamReviewApiService: PerformanceTeamReviewApiService, private store: Store) {
        super(null, 0);
    }

    @Action(FetchPerformanceTeamReviews)
    fetchPerformanceTeamReviews(ctx: StateContext<PerformanceTeamReviewsStateModel>, action: FetchPerformanceTeamReviews): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetPerformanceTeamReviews,
            FetchPerformanceTeamReviewsSuccess,
            FetchPerformanceTeamReviewsFailure,
            this._performanceTeamReviewApiService
                .getPerformanceTeamReviews(
                    action.payload?.teamId,
                    action.payload?.pageSize,
                    action.payload?.pageNumber,
                    action.payload?.search,
                    action.payload?.type,
                    action.payload?.periodUnderReview,
                    action.payload?.goalSettingPeriod,
                    action.payload?.userId
                )
                .pipe(
                    map((query) => {
                        ctx.patchState({
                            totalCount: query.pagination.number_of_results,
                        });

                        return query.data.map((query) => new PerformanceTeamReview(query));
                    })
                )
        );
    }

    @Action(FetchPerformanceTeamReviewsSuccess)
    fetchAllSuccess(ctx: StateContext<PerformanceTeamReviewsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchPerformanceTeamReviewsFailure)
    fetchAllFailure(ctx: StateContext<PerformanceTeamReviewsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(AddPerformanceTeamReviews)
    addEntities({ getState, patchState }: StateContext<PerformanceTeamReviewsStateModel>, { payload }: AddPerformanceTeamReviews): void {
        const state = getState();

        const subentities = payload.map((subentity) => subentity.id);
        const newList = Array.from(new Set<string>([...(state.list || []), ...subentities]));

        patchState({
            list: newList,
            filtered: [...newList], // filtered is same as list because of serverside pagination
            selected: [],
        });
    }

    @Action(CreatePerformanceTeamReviewSuccess)
    createEntity(ctx: StateContext<PerformanceTeamReviewsStateModel>, action: CreatePerformanceTeamReviewSuccess): void {
        const tags = this.store.selectSnapshot(PerformanceTeamReviewState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, tags);
    }

    @Action(SetPerformanceTeamReviews)
    setEntities(ctx: StateContext<PerformanceTeamReviewsStateModel>, action: SetPerformanceTeamReviews): void {
        this._setSubentities(ctx, action, AddPerformanceTeamReviews);
    }

    @Action(SearchPerformanceTeamReviews)
    search({ patchState }: StateContext<PerformanceTeamReviewsStateModel>, { payload }: SearchPerformanceTeamReviews): void {
        patchState({
            searchTerm: payload,
        });
    }
}
